/**
 * Profile Badges Tab
 * Shows user earned badges
 */

import { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material'
import {
  Close,
  EmojiEvents,
} from '@mui/icons-material'
import {
  useMyBadges,
  useUserBadges,
} from '../hooks/useAchievements'
import { getImageUrl } from '@/utils/imageUtils'

// ===================
// TYPES
// ===================

interface Badge {
  id: string
  name: string
  description: string
  imageUrl: string | null
  earnedDate: string
  collectionName: string | null
}

// ===================
// COMPONENT
// ===================

interface ProfileBadgesTabProps {
  userId?: string
  isOwnProfile?: boolean
}

export function ProfileBadgesTab({
  userId,
  isOwnProfile = true,
}: ProfileBadgesTabProps) {
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null)

  // Fetch badges for own profile
  const {
    data: ownBadges,
    isLoading: isLoadingOwnBadges,
    error: ownBadgesError,
  } = useMyBadges()

  // Fetch badges for other users
  const {
    data: otherUserBadges,
    isLoading: isLoadingOtherBadges,
    error: otherBadgesError,
  } = useUserBadges(isOwnProfile ? undefined : userId)

  // Determine loading/error state
  const isLoading = isOwnProfile ? isLoadingOwnBadges : isLoadingOtherBadges
  const error = isOwnProfile ? ownBadgesError : otherBadgesError

  // Get badges from API for both own profile and other users
  const badges = useMemo((): Badge[] => {
    const badgeData = isOwnProfile ? ownBadges : otherUserBadges
    if (!badgeData) return []

    return badgeData.map((badge) => ({
      id: badge.id,
      name: badge.name,
      description: badge.description || 'Badge earned',
      imageUrl: badge.imageUrl,
      earnedDate: badge.earnedDate,
      collectionName: badge.sourceName,
    }))
  }, [isOwnProfile, ownBadges, otherUserBadges])

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load badges. Please try again later.
      </Alert>
    )
  }

  return (
    <Box>
      {/* Badges Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
          Badges Earned
        </Typography>

        {badges.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <EmojiEvents sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography color="text.secondary">
              No badges earned yet. Complete adventure collections to earn badges!
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {badges.map((badge) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={badge.id}>
                <Box
                  onClick={() => setSelectedBadge(badge)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 1.5,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      transform: 'scale(1.05)',
                      boxShadow: 2,
                    },
                  }}
                >
                  {/* Badge Image - 64x64 */}
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #ffd700 0%, #ffb700 100%)',
                      boxShadow: '0 2px 8px rgba(255, 183, 0, 0.4)',
                      mb: 1,
                    }}
                  >
                    {badge.imageUrl ? (
                      <Box
                        component="img"
                        src={getImageUrl(badge.imageUrl)!}
                        alt={badge.name}
                        sx={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }}
                      />
                    ) : (
                      <EmojiEvents sx={{ fontSize: 36, color: 'white' }} />
                    )}
                  </Box>

                  {/* Badge Name - max 40 characters, truncate */}
                  <Typography
                    variant="caption"
                    sx={{
                      textAlign: 'center',
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: 1.3,
                      maxHeight: '2.6em',
                      width: '100%',
                    }}
                    title={badge.name}
                  >
                    {badge.name.length > 40 ? badge.name.slice(0, 40) + '...' : badge.name}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Badge Detail Dialog */}
      <Dialog
        open={!!selectedBadge}
        onClose={() => setSelectedBadge(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedBadge && (
          <>
            <DialogTitle sx={{ textAlign: 'center', pb: 0, pt: 3 }}>
              <IconButton
                onClick={() => setSelectedBadge(null)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <Close />
              </IconButton>

              {/* Badge Image */}
              <Box
                sx={{
                  width: 96,
                  height: 96,
                  margin: '0 auto 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #ffd700 0%, #ffb700 100%)',
                  boxShadow: '0 4px 16px rgba(255, 183, 0, 0.4)',
                }}
              >
                {selectedBadge.imageUrl ? (
                  <Box
                    component="img"
                    src={getImageUrl(selectedBadge.imageUrl)!}
                    alt={selectedBadge.name}
                    sx={{ width: 88, height: 88, borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  <EmojiEvents sx={{ fontSize: 56, color: 'white' }} />
                )}
              </Box>

              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {selectedBadge.name}
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary" paragraph>
                {selectedBadge.description}
              </Typography>

              <Divider sx={{ my: 2 }} />

              {selectedBadge.collectionName && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  From collection: <strong>{selectedBadge.collectionName}</strong>
                </Typography>
              )}

              <Typography variant="body2" color="primary">
                Earned on {new Date(selectedBadge.earnedDate).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
              <Button variant="contained" onClick={() => setSelectedBadge(null)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  )
}
